import { inject, PLATFORM_ID } from '@angular/core';
import {
  HttpRequest,
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpHandlerFn
} from '@angular/common/http';
import { throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take, finalize } from 'rxjs/operators';
import { StorageService } from '../services/storage.service';
import { AuthService } from '../services/auth.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AuthComponent } from '../components/auth/auth.component';
import { isPlatformBrowser } from '@angular/common';
import { API_ENDPOINTS } from '../shared/constants/api.constants';

// Shared refresh state: concurrent 401s wait for one refresh instead of each
// firing their own.
let isRefreshing = false;
// null = refresh in progress, false = refresh failed, string = new access token.
const refreshTokenSubject = new BehaviorSubject<string | null | false>(null);

/** Auth endpoints never get the refresh-and-retry treatment, otherwise a 401
 *  from the refresh call itself would wait on its own refresh forever. */
const NO_REFRESH_URLS = [API_ENDPOINTS.REFRESH_TOKEN, API_ENDPOINTS.GET_LOGIN_OTP, API_ENDPOINTS.VERIFY_OTP];

export const AuthInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
  const storage = inject(StorageService);
  const authService = inject(AuthService);
  const modalService = inject(NgbModal);
  const platformId = inject(PLATFORM_ID);

  // SSR-safe token retrieval
  const token = storage.getItem('access_token');
  const isAuthEndpoint = NO_REFRESH_URLS.some(url => req.url.startsWith(url));
  let authReq = req;

  if (token && !req.url.startsWith(API_ENDPOINTS.REFRESH_TOKEN)) {
    authReq = req.clone({
      setHeaders: { Authorization: `Bearer ${token}` },
    });
  }

  const withToken = (accessToken: string) =>
    authReq.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only attempt a refresh in the browser, for requests that were authenticated.
      if (error.status !== 401 || !isPlatformBrowser(platformId) || isAuthEndpoint || !token) {
        return throwError(() => error);
      }

      if (isRefreshing) {
        // Another request is already refreshing - wait for its token, then retry once.
        return refreshTokenSubject.pipe(
          filter(t => t !== null),
          take(1),
          switchMap(t => t ? next(withToken(t)) : throwError(() => error))
        );
      }

      isRefreshing = true;
      refreshTokenSubject.next(null);

      return authService.refreshToken().pipe(
        catchError(err => {
          refreshTokenSubject.next(false);
          isRefreshing = false;
          // Session can't be renewed: sign out cleanly (updates header/cart state)
          // and ask once for login rather than opening a modal per failed request.
          authService.logout();
          if (!modalService.hasOpenModals()) {
            modalService.open(AuthComponent, { ariaLabelledBy: 'auth-modal-title', centered: true, size: 'sm' });
          }
          return throwError(() => err);
        }),
        // Also covers the refresh being cancelled (e.g. navigation unsubscribes).
        finalize(() => { isRefreshing = false; }),
        switchMap((res: any) => {
          isRefreshing = false;
          authService.saveAccessToken(res.access);
          if (res.refresh) {
            storage.setItem('refresh_token', res.refresh);
          }
          refreshTokenSubject.next(res.access);
          // Errors from the retried request propagate normally - they are not auth failures.
          return next(withToken(res.access));
        })
      );
    })
  );
};
